/**
 * Base class for all download action items.
 * Contains common properties and methods shared across all download document types.
 */
export abstract class BaseActionItem {
  status?: string;
  id?: string;
  environment?: string;

  /**
   * Returns a human-readable display value for this item.
   * Used in UI to show status or name.
   */
  abstract getDisplayValue(): string;
}

/**
 * Download document for IP Cases (Patents, Trademarks, Matters).
 * Extends BaseActionItem to inherit common properties.
 */
export class CaseDownloadDocument extends BaseActionItem {
  override id?: string;
  localDocument?: string;

  case?: string;
  destCase?: string;
  trifold?: string;
  destTrifold?: string;
  documentName?: string;
  type?: string;
  title?: string;
  date?: string;
  keywords?: string;
  note?: string;

  getDisplayValue(): string {
    return this.documentName || this.case || 'Document';
  }
}

/**
 * Download document for Patent References.
 * Represents references cited in patents (US Patent, Foreign Patent, NPL).
 * Extends BaseActionItem to inherit common properties.
 */
export class ReferenceDownloadDocument extends BaseActionItem {
  override id?: string;
  localDocument?: string;

  type?: string;
  referenceName?: string;
  documentName?: string;

  patentNumber?: string;
  pubNumber?: string;
  pubDate?: string;
  issueDate?: string;
  applicant?: string;
  inventor?: string;
  assignee?: string;
  countryCode?: string;
  kindCode?: string;
  foreignDocumentNumber?: string;

  nameOfAuthor?: string;
  titleOfArticle?: string;
  titleOfItem?: string;
  publisher?: string;
  pages?: string;
  city?: string;
  volume?: string;
  nplDate?: string;

  keywords?: string;
  note?: string;
  customCitation?: string;

  getDisplayValue(): string {
    return this.documentName || this.referenceName || 'Reference';
  }
}

/**
 * Download document for Trademark Images/Graphics.
 * Extends BaseActionItem to inherit common properties.
 */
export class TrademarkImageDownloadDocument extends BaseActionItem {
  override id?: string;
  case?: string;

  documentName?: string;
  localDocument?: string;

  getDisplayValue(): string {
    return this.documentName || this.case || 'Trademark Image';
  }
}

/**
 * Download document for External Links (Case Links).
 * Represents external links to case documents retrieved via LinkRetrieval.
 * Extends BaseActionItem to inherit common properties.
 */
export class ExternalLinkDownloadDocument extends BaseActionItem {
  override environment?: string;
  override status?: string;
  case?: string;
  link?: string;

  getDisplayValue(): string {
    return this.link || this.case || 'External Link';
  }
}

/**
 * Download document for Reference Associations.
 * Represents associations between cases and references (IDS, cited dates, etc.).
 * Extends BaseActionItem to inherit common properties.
 */
export class ReferenceAssociationDownloadDocument extends BaseActionItem {
  override id?: string;

  case?: string;
  referenceName?: string;
  includedInIds?: string;
  acknowledged?: string;
  source?: string;
  sourceDate?: string;
  citedDate?: string;
  note?: string;

  getDisplayValue(): string {
    return this.referenceName || this.case || 'Reference Association';
  }
}

/**
 * Union type representing any of the five download document types.
 * Use this when you need to accept "any type of download document".
 *
 * Example:
 *   const doc: DownloadDocument = { ... };
 *   TypeScript will check if it matches one of the four types.
 */
export type DownloadDocument =
  | CaseDownloadDocument
  | ReferenceDownloadDocument
  | TrademarkImageDownloadDocument
  | ExternalLinkDownloadDocument
  | ReferenceAssociationDownloadDocument;
